import React from 'react';
import { AlertTriangle, ShoppingCart, BookOpen } from 'lucide-react';
import { LeadFormModal } from '../components/LeadFormModal';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { Breadcrumb } from '../components/Breadcrumb';
import { WhatNextSection } from '../components/WhatNextSection';
import { ParticlesBackground } from '../components/effects/ParticlesBackground';
import { AnimatedBlob } from '../components/effects/AnimatedBlob';
import { AnimatedGrid } from '../components/effects/AnimatedGrid';
import { RevealOnScroll } from '../components/effects/RevealOnScroll';
import { GlassCard } from '../components/ui/GlassCard';

export const AmazonListerAiPage: React.FC = () => {
    usePageMetadata({
        title: "Amazon Lister AI [Case Study] | Automatyzacja Sprzedaży",
        description: "Poznaj case study naszego projektu automatyzacji sprzedaży na Amazon. Zobacz, jak AI znajdowało produkty, tworzyło listingi i optymalizowało ceny.",
        schema: {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Case Study: Amazon Lister AI",
            "description": "Studium przypadku systemu AI, który optymalizował listingi, ceny i zarządzał zapasami na Amazon.",
            "url": "https://biznesbezklikania.pl/rozwiazania/amazon-lister-ai"
        }
    });

    return (
        <>
            {/* Hero Section */}
            <section className="relative min-h-[50vh] flex items-center overflow-hidden py-20 md:py-24">
                <ParticlesBackground variant="minimal" />
                <AnimatedBlob variant="mixed" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-bkg-light/50 to-bkg-light dark:via-bkg-dark/50 dark:to-bkg-dark pointer-events-none" />

                <div className="container mx-auto px-4 text-center relative z-10">
                    <RevealOnScroll>
                        <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-orange-500 to-yellow-500 flex items-center justify-center">
                            <ShoppingCart className="w-10 h-10 text-white" />
                        </div>
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 mb-4">
                            <BookOpen className="w-4 h-4 text-amber-500" />
                            <span className="text-sm font-medium text-amber-500">Case Study</span>
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight bg-gradient-to-r from-orange-500 via-yellow-500 to-orange-500 bg-clip-text text-transparent bg-[length:200%_200%] animate-gradient">
                            Amazon Lister AI
                        </h1>
                    </RevealOnScroll>

                    <RevealOnScroll delay={100}>
                        <p className="text-lg md:text-xl text-text-secondary-light dark:text-text-secondary-dark max-w-3xl mx-auto">
                            Poznaj historię naszego zamkniętego projektu, który automatyzował sprzedaż na Amazon. Dowiedz się, jak AI potrafiło znajdować produkty, tworzyć listingi i generować przychód.
                        </p>
                    </RevealOnScroll>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-bkg-light dark:from-bkg-dark to-transparent pointer-events-none" />
            </section>

            <Breadcrumb />

            {/* Content Section */}
            <section className="py-16 md:py-20 relative">
                <AnimatedGrid variant="dots" animated={false} />
                <div className="container mx-auto px-4 max-w-4xl relative z-10">
                    {/* Warning Banner */}
                    <RevealOnScroll>
                        <GlassCard variant="subtle" className="mb-12 bg-amber-500/10 border-l-4 border-amber-500">
                            <div className="flex items-start gap-4">
                                <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-1" />
                                <div>
                                    <h3 className="font-bold text-amber-600 dark:text-amber-400">Projekt zamknięty</h3>
                                    <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                                        Ten produkt nie jest już dostępny w sprzedaży z powodu zmian w polityce Amazona. Prezentujemy go jako studium przypadku naszych możliwości w zakresie automatyzacji e-commerce.
                                    </p>
                                </div>
                            </div>
                        </GlassCard>
                    </RevealOnScroll>

                    <RevealOnScroll delay={100} className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-text-primary-light dark:text-text-primary-dark">
                            Case Study: Potęga AI w E-commerce
                        </h2>
                        <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark mt-4">
                            Co potrafił nasz system i jakie wnioski wyciągnęliśmy z tego projektu.
                        </p>
                    </RevealOnScroll>

                    <RevealOnScroll delay={200}>
                        <GlassCard variant="medium" className="prose dark:prose-invert max-w-none">
                            <p className="text-text-secondary-light dark:text-text-secondary-dark">
                                Projekt Amazon Lister AI był naszym ambitnym przedsięwzięciem mającym na celu pełną automatyzację procesu sprzedaży na największej platformie e-commerce na świecie. System opierał się na kilku kluczowych modułach:
                            </p>
                            <ul className="space-y-4 my-6">
                                <li className="text-text-secondary-light dark:text-text-secondary-dark">
                                    <strong className="text-text-primary-light dark:text-text-primary-dark">Product Research:</strong> AI analizowało trendy sprzedażowe i dane BSR (Best Sellers Rank), aby identyfikować nisze produktowe z wysokim potencjałem i niską konkurencją.
                                </li>
                                <li className="text-text-secondary-light dark:text-text-secondary-dark">
                                    <strong className="text-text-primary-light dark:text-text-primary-dark">Listing Creation:</strong> Na podstawie słów kluczowych system generował zoptymalizowane pod kątem SEO tytuły, punkty (bullet points) i opisy, które konwertowały lepiej niż 90% ofert tworzonych ręcznie.
                                </li>
                                <li className="text-text-secondary-light dark:text-text-secondary-dark">
                                    <strong className="text-text-primary-light dark:text-text-primary-dark">Image Generation:</strong> Wykorzystując modele dyfuzyjne, AI tworzyło realistyczne zdjęcia produktowe, infografiki i content A+ bez potrzeby sesji zdjęciowej.
                                </li>
                                <li className="text-text-secondary-light dark:text-text-secondary-dark">
                                    <strong className="text-text-primary-light dark:text-text-primary-dark">Dynamic Pricing:</strong> Algorytm monitorował ceny konkurencji i dostosowywał nasze ceny w czasie rzeczywistym, aby maksymalizować zyski i utrzymywać "Buy Box".
                                </li>
                            </ul>

                            <GlassCard variant="subtle" className="border-l-4 border-primary-light dark:border-primary-dark my-6">
                                <p className="italic text-text-secondary-light dark:text-text-secondary-dark">
                                    "MVP systemu wygenerowało 50,000 zł przychodu w 3 miesiące, zanim zmiany w regulaminie Amazon zmusiły nas do zamknięcia projektu. To dowód na ogromny, wciąż niewykorzystany potencjał AI w e-commerce."
                                </p>
                            </GlassCard>

                            <p className="text-text-secondary-light dark:text-text-secondary-dark">
                                Chociaż produkt nie jest już dostępny, wiedza i doświadczenie zdobyte przy jego tworzeniu pozwalają nam wdrażać niezwykle skuteczne, niestandardowe automatyzacje dla sprzedawców internetowych. Jeśli chcesz dowiedzieć się więcej, skontaktuj się z nami.
                            </p>
                        </GlassCard>
                    </RevealOnScroll>

                    <RevealOnScroll delay={300}>
                        <div className="text-center mt-12">
                            <LeadFormModal
                                productSlug="amazon-lister-ai"
                                triggerLabel="Porozmawiaj o automatyzacji e-commerce"
                                triggerClassName="px-8 py-4 font-semibold text-white bg-gradient-to-r from-orange-500 to-yellow-500 rounded-xl hover:shadow-xl hover:shadow-orange-500/30 hover:scale-[1.02] transition-all duration-300 text-lg inline-flex items-center group"
                            />
                        </div>
                    </RevealOnScroll>
                </div>
            </section>

            <WhatNextSection diyLink="/rozwiazania/amazon-lister-ai" />
        </>
    );
};
